import React, { useState } from "react";
import { submitBmiBenefitCheck, submitBmiClaim } from "../api/medikredit/benefitCheckApi";
import styles from "../css/components/EligibilityButton.module.css";

/**
 * Submits the consultation's BMI measurement to MediKredit as a Pharmacy
 * Benefit Check with Clinical Results (tx_cd 40) — per MediKredit's own
 * confirmed accreditation example (PROC tar_cd 07111, CLINICAL type 14 /
 * subtype 12). The practice's tariff price for this service isn't stored
 * anywhere yet, so it's entered here rather than guessed.
 */
export default function BmiBenefitCheckButton({ patientId, bmi, onResult }) {
  const [price, setPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [claimLoading, setClaimLoading] = useState(false);
  const [claimResult, setClaimResult] = useState(null);
  const [claimError, setClaimError] = useState(null);

  const bmiNumber = Number(bmi);
  const canSubmit = !!patientId && Number.isFinite(bmiNumber) && bmiNumber > 0 && Number(price) > 0;

  async function run() {
    setLoading(true);
    setError(null);
    setResult(null);
    setClaimResult(null);
    setClaimError(null);
    try {
      const data = await submitBmiBenefitCheck({ patientId, bmi: bmiNumber, price: Number(price) });
      setResult(data);
      if (onResult) onResult(data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Benefit check submission failed");
    } finally {
      setLoading(false);
    }
  }

  async function runClaim() {
    setClaimLoading(true);
    setClaimError(null);
    try {
      const data = await submitBmiClaim({
        patientId,
        bmi: bmiNumber,
        price: Number(price),
        txNumber: result?.transactionNumber,
      });
      setClaimResult(data);
    } catch (e) {
      setClaimError(e?.response?.data?.message || e.message || "Claim submission failed");
    } finally {
      setClaimLoading(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.benefitCheckRow}>
        <div className={styles.priceField}>
          <label htmlFor="bmiBenefitCheckPrice" className={styles.priceLabel}>
            Tariff price
          </label>
          <input
            id="bmiBenefitCheckPrice"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className={styles.priceInput}
          />
        </div>

        <button
          type="button"
          className={`${styles.btn} ${styles.btnCompact}`}
          onClick={run}
          disabled={loading || !canSubmit}
          title={!canSubmit ? "Enter a BMI value and tariff price first" : undefined}
        >
          {loading ? (
            <>
              <span className={styles.spinner} />
              Submitting…
            </>
          ) : (
            "Submit to Medical Aid"
          )}
        </button>
      </div>

      {result && (
        <div className={styles.stepCard}>
          <StepRow
            label="1. Benefit check"
            approved={result.approved}
            authNumber={result.authNumber}
            messages={result.messages}
            styles={styles}
          />

          {result.approved && (
            <div className={styles.stepRow}>
              <span className={`${styles.stepIcon} ${claimResult ? "" : styles.stepIconPending}`}>
                {claimResult ? (claimResult.approved ? "✓" : "✕") : "2"}
              </span>
              <div className={styles.stepBody}>
                <span className={styles.stepLabel}>2. Claim for payment</span>
                {!claimResult && (
                  <button
                    type="button"
                    className={`${styles.btn} ${styles.btnCompact}`}
                    onClick={runClaim}
                    disabled={claimLoading}
                  >
                    {claimLoading ? (
                      <>
                        <span className={styles.spinner} />
                        Submitting…
                      </>
                    ) : (
                      "Submit Claim for Payment"
                    )}
                  </button>
                )}
                {claimResult && claimResult.authNumber && (
                  <span className={styles.authChip}>Switch ref (Hnet) · {claimResult.authNumber}</span>
                )}
                {claimResult && !claimResult.approved && claimResult.messages?.length > 0 && (
                  <ul className={styles.reasons}>
                    {claimResult.messages.map((m, i) => (
                      <li key={i}>
                        {m.desc} <span className={styles.code}>({m.code})</span>
                      </li>
                    ))}
                  </ul>
                )}
                {claimError && <span className={styles.stepError}>{claimError}</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className={`${styles.result} ${styles.errorBox}`}>
          <div className={styles.resultBody}>
            <span className={styles.resultTitle}>Couldn't submit</span>
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StepRow({ label, approved, authNumber, messages, styles }) {
  return (
    <div className={styles.stepRow}>
      <span className={`${styles.stepIcon} ${approved ? styles.stepIconOk : styles.stepIconFail}`}>
        {approved ? "✓" : "✕"}
      </span>
      <div className={styles.stepBody}>
        <span className={styles.stepLabel}>{label}</span>
        {approved && authNumber && (
          <span className={styles.authChip}>Switch ref (Hnet) · {authNumber}</span>
        )}
        {!approved && messages && messages.length > 0 && (
          <ul className={styles.reasons}>
            {messages.map((m, i) => (
              <li key={i}>
                {m.desc} <span className={styles.code}>({m.code})</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
